import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Bell, Globe, Database, Shield } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import Select from '../components/ui/Select';
import { useTheme } from '../contexts/ThemeContext';
import { useLocalStorage } from '../hooks/useLocalStorage';

const Settings = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [savedSettings, setSavedSettings] = useLocalStorage('trendy_settings', {
    notifications: true,
    autoRefresh: true,
    refreshInterval: '30',
    defaultRegion: 'global',
    defaultCategory: 'all',
    dataRetention: '30',
  });
  
  const [settings, setSettings] = useState(savedSettings);

  useEffect(() => {
    setSettings(savedSettings);
  }, []);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleSave = () => {
    setShowConfirmModal(true);
  };

  const confirmSave = () => {
    // Save settings to localStorage
    setSavedSettings(settings);
    setShowConfirmModal(false);
    setShowSuccessModal(true);
    setTimeout(() => setShowSuccessModal(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Settings</h1>
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Customize your Trendy experience
        </p>
      </div>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-reddit-orange" />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Theme</label>
            <div className="flex gap-4">
              <Button
                variant={!isDarkMode ? 'primary' : 'outline'}
                onClick={() => isDarkMode && toggleTheme()}
              >
                Light Mode
              </Button>
              <Button
                variant={isDarkMode ? 'primary' : 'outline'}
                onClick={() => !isDarkMode && toggleTheme()}
              >
                Dark Mode
              </Button>
            </div>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mt-2">
              Choose your preferred color scheme
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-reddit-blue" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Enable Notifications</p>
              <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                Receive alerts for trending topics
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={settings.notifications}
                onChange={(e) => handleSettingChange('notifications', e.target.checked)}
              />
              <div className="w-11 h-6 bg-reddit-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-reddit-orange/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-reddit-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-reddit-orange"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Auto Refresh</p>
              <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                Automatically update trending data
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={settings.autoRefresh}
                onChange={(e) => handleSettingChange('autoRefresh', e.target.checked)}
              />
              <div className="w-11 h-6 bg-reddit-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-reddit-orange/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-reddit-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-reddit-orange"></div>
            </label>
          </div>

          {settings.autoRefresh && (
            <div>
              <label className="block text-sm font-medium mb-2">Refresh Interval</label>
              <Select
                value={settings.refreshInterval}
                onChange={(e) => handleSettingChange('refreshInterval', e.target.value)}
              >
                <option value="15">15 seconds</option>
                <option value="30">30 seconds</option>
                <option value="60">1 minute</option>
                <option value="300">5 minutes</option>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-reddit-green" />
            Default Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Default Region</label>
            <Select
              value={settings.defaultRegion}
              onChange={(e) => handleSettingChange('defaultRegion', e.target.value)}
            >
              <option value="global">Global</option>
              <option value="north-america">North America</option>
              <option value="europe">Europe</option>
              <option value="asia">Asia</option>
              <option value="south-america">South America</option>
              <option value="africa">Africa</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Default Category</label>
            <Select
              value={settings.defaultCategory}
              onChange={(e) => handleSettingChange('defaultCategory', e.target.value)}
            >
              <option value="all">All Categories</option>
              <option value="technology">Technology</option>
              <option value="politics">Politics</option>
              <option value="sports">Sports</option>
              <option value="entertainment">Entertainment</option>
              <option value="business">Business</option>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Data & Privacy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-reddit-orange" />
            Data & Privacy
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Data Retention</label>
            <Select
              value={settings.dataRetention}
              onChange={(e) => handleSettingChange('dataRetention', e.target.value)}
            >
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="365">1 year</option>
            </Select>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mt-2">
              How long to keep historical trend data
            </p>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1">
              Clear Cache
            </Button>
            <Button variant="outline" className="flex-1">
              Export Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* API Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-reddit-blue" />
            API Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">API Endpoints Status</label>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 rounded-lg bg-reddit-50 dark:bg-dark-border">
                <span className="text-sm">Twitter API</span>
                <span className="text-sm text-reddit-green font-medium">● Connected</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-reddit-50 dark:bg-dark-border">
                <span className="text-sm">Reddit API</span>
                <span className="text-sm text-reddit-green font-medium">● Connected</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-reddit-50 dark:bg-dark-border">
                <span className="text-sm">Google Trends API</span>
                <span className="text-sm text-reddit-green font-medium">● Connected</span>
              </div>
            </div>
          </div>

          <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
            All data sources are currently operational
          </p>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-4">
        <Button variant="outline">Reset to Defaults</Button>
        <Button variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </div>

      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Changes"
        onConfirm={confirmSave}
      >
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Are you sure you want to save these changes?
        </p>
      </Modal>

      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success!"
        type="success"
        hideActions={true}
      >
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Your settings have been saved successfully!
        </p>
      </Modal>
    </div>
  );
};

export default Settings;